import { useSwitch } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useCallback, useContext } from 'react';

import CheckIcon from '../../../../assets/icons/check.svg?react';
import IndeterminateIcon from '../../../../assets/icons/indeterminate.svg?react';
import { useHover } from '../../../utils/hooks';
import Icon from '../../foundations/icon/icon';
import { CheckboxGroupContext } from '../checkbox-group/checkbox-group-context';

import type { ForwardedRef, ReactElement } from 'react';
import type React from 'react';

import type { CheckboxProps } from './checkbox.types';

import './checkbox.scss';

/**
 * Checkboxes are used to pick an option when one or more options can be chosen.
 * A single checkbox can be used representing a singular option,
 * for the purpose of toggling an element between an active and inactive state.
 *
 * ### Usage
 *
 * ```tsx
 * import { useState } from 'react';
 * import { Checkbox } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   const [checked, setChecked] = useState(true);
 *
 *   return <Checkbox {...args} checked onChange((event) => setChecked(event.target.checked)) />
 * }
 * ```
 */

const Checkbox = forwardRef(
  (props: CheckboxProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
    const {
      checked: checkedProp,
      className,
      defaultChecked,
      disabled: disabledProp = false,
      indeterminate = false,
      inputProps,
      onChange,
      readOnly: readOnlyProp = false,
      required: requiredProp = false,
      ...rest
    } = props;

    const checkboxGroupContext = useContext(CheckboxGroupContext);

    const useCheckboxProps = {
      checked: checkedProp,
      defaultChecked,
      disabled: disabledProp || checkboxGroupContext?.disabled,
      onChange,
      readOnly: readOnlyProp || checkboxGroupContext?.readOnly,
      required: requiredProp,
    };

    const { checked, disabled, focusVisible, getInputProps, readOnly } = useSwitch(useCheckboxProps);

    const handleClick = useCallback(
      (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>): void => {
        if (readOnly) {
          event.preventDefault();
        }
      },
      [readOnly],
    );

    const inputPropsInternal = getInputProps({
      onClick: handleClick,
      role: undefined,
      type: 'checkbox',
      ...inputProps,
      className: cn('rbui-checkbox__input', inputProps?.className),
    });

    const { handlers, hovered } = useHover();

    const active = checked || indeterminate;

    const SvgIcon = indeterminate ? <IndeterminateIcon /> : <CheckIcon />;

    const classes = cn('rbui-checkbox', className, {
      'rbui-checkbox--checked': active,
      'rbui-checkbox--disabled': disabled,
      'rbui-checkbox--focus-visible': focusVisible && !disabled,
      'rbui-checkbox--hovered': hovered,
      'rbui-checkbox--readonly': readOnly,
    });

    return (
      <span className={classes} {...rest} {...handlers}>
        <div aria-hidden className="rbui-checkbox__inner">
          {active && <Icon>{SvgIcon}</Icon>}
        </div>
        <input {...inputPropsInternal} ref={forwardedRef} />
      </span>
    );
  },
);

Checkbox.displayName = 'Checkbox';
export default Checkbox;
